import type {
	CommentaryTemplateDto,
	InvestmentCommentaryResponse,
	InvestmentCommentaryResponseStatusEnum,
	InvestmentSummary,
} from "@eu.mdotm.api/sphere";
import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting";
import AuthorizationGuard, { hasAccess } from "$root/components/AuthorizationGuard";
import { ButtonWithSelect } from "$root/components/buttons/ButtonWithSelect";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { type FakeAiLoaderBaseProps, FakeAiLoaderBase } from "$root/components/FakeAiLoader";
import {
	simulateAnimationProgress,
	useAnimationProgressById,
	useAnimationProgressState,
} from "$root/components/FakeAiLoader/atom";
import { type FakeAiLoaderStep, useFakeAiLoader } from "$root/components/FakeAiLoader/common";
import { IconWalls } from "$root/components/IconWall";
import { type MarkdownRendererProps, MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { START_DATE_CREATE_PROPOSAL_COMMENTARY } from "$root/functional-areas/const";
import { reapplySphereMetricsWithConfirmationDialog } from "$root/functional-areas/portfolio/metrics";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { storyfolioRepository } from "$root/functional-areas/storyfolio/repository";
import { useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { PortfolioDetailsTabs } from "$root/pages/PortfolioDetails/portfolio-details-tabs";
import {
	PortfolioWidgetBase,
	useAsOfSnapshotWall,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type ContextContent, differenceInDaysISO, qualifier, seconds, withContext } from "$root/utils";
import { useMappedQuery, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import {
	useWidgetLoading,
	useWidgetOptions,
	useWidgetWalled,
} from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type Option,
	type StylableProps,
	ActionText,
	Banner,
	ClickableDiv,
	CollapsibleBase,
	Column,
	Icon,
	Row,
	ScrollWrapper,
	Text,
} from "@mdotm/shadcn/legacy/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { DropdownMenu } from "@mdotm/shadcn/mdotm/components";
import { atomWithStorage, useAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type SetterOrUpdater, noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PortfolioContext } from "../contexts/portfolio";
import { type AsOfSnapshotState, isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";

function PortfolioCommentaryBodyMDBlockV2(props: {
	data: InvestmentCommentaryResponse;
	ctx: ContextContent<typeof PortfolioContext>;
	templates: CommentaryTemplateDto[];
	onGenerate(selected: string): MaybePromise<void>;
	selected: string;
	cachedCommentaryStatus: InvestmentCommentaryResponseStatusEnum | null;
	loaderProps: FakeAiLoaderBaseProps;
}) {
	const { data, ctx, templates, selected, cachedCommentaryStatus } = props;
	const { commentary, commentaryDate, status, template, usedCustomMetrics, metadataLastUpdate, prevUsedCustomMetrics } =
		data;
	const { portfolio, enhanced, asOf } = ctx;
	const { formatDateTime } = useLocaleFormatters();

	const options = useMemo(
		(): Array<Option<string>> =>
			templates?.flatMap((x) => (x.name && x.uuid && x.visible ? [{ label: x.name, value: x.uuid }] : [])) ?? [],
		[templates],
	);

	const canUserGenerateNewCommentary = useMemo(() => {
		// Pinned to a past as-of date: generating would mutate the live commentary (R13).
		if (asOf != null) {
			return false;
		}
		if (enhanced) {
			return differenceInDaysISO(portfolio?.lastActionDate, START_DATE_CREATE_PROPOSAL_COMMENTARY) > 0;
		}

		return true;
	}, [asOf, enhanced, portfolio?.lastActionDate]);

	return (
		<>
			<ScrollWrapper>
				{(usedCustomMetrics || prevUsedCustomMetrics) && (
					<ReapplySphereMetricsBanner className="mb-4" portfolio={portfolio} metadataLastUpdate={metadataLastUpdate} />
				)}
				<MarkdownRenderer componentOverrides={markdownOverrides}>{commentary ?? ""}</MarkdownRenderer>
			</ScrollWrapper>
			<div className="py-1 mt-auto">
				{cachedCommentaryStatus === "CALCULATING" ? (
					<FakeAiLoaderBase {...props.loaderProps} />
				) : (
					<div className="flex justify-between py-1 mt-auto">
						{commentaryDate && (
							<div className="flex gap-1">
								<Icon icon="Clock" color={themeCSSVars.palette_N300} size={16} className="my-auto" />
								<div>
									<p className="whitespace-pre-line">
										<Text type="Body/S/Book">Generated on </Text>
										<Text type="Body/S/Bold" data-qualifier={qualifier.widgets.portfolioCommentary.timeStamp}>
											{formatDateTime(commentaryDate, { omitYear: true })}
										</Text>
									</p>
									<p className="whitespace-pre-line">
										<Text type="Body/S/Book">Template </Text>
										<Text type="Body/S/Bold" data-qualifier={qualifier.widgets.portfolioCommentary.templateName}>
											{template?.name}
										</Text>
									</p>
								</div>
							</div>
						)}
						<AuthorizationGuard
							permissionChecker={aclByArea.portfolio.canEditComposition}
							acl={portfolio?.richAcl?.acl ?? []}
						>
							{canUserGenerateNewCommentary && (
								<div className="flex items-center gap-2">
									<ButtonWithSelect
										options={options}
										value={selected}
										disabled={
											portfolio?.status === "CALCULATING" || portfolio?.status === "ERROR" || status === "CALCULATING"
										}
										palette="secondary"
										size="small"
										onClick={props.onGenerate}
										enableSearch
										dataAttributes={{
											buttonDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.generate },
											selectDataAttributes: { "data-qualifier": qualifier.widgets.portfolioCommentary.selectTemplate },
											selectTriggerDataAttributes: {
												"data-qualifier": qualifier.widgets.portfolioCommentary.selectTemplate + "/Trigger",
											},
										}}
									>
										Generate
									</ButtonWithSelect>
								</div>
							)}
						</AuthorizationGuard>
					</div>
				)}
			</div>
		</>
	);
}

export function usePersistentFakeAiLoaderAnimation(portfolioUid: string): {
	fakeLoaderState: FakeAiLoaderStep;
	stopFakeLoader: () => Promise<void>;
	restartFakeLoader: () => Promise<void>;
} {
	const commentaryGenerationProgress = useAnimationProgressById(portfolioUid ?? "-");
	const { setAnimationProgress } = useAnimationProgressState();

	const initialFakeLoaderState = useMemo(
		() =>
			commentaryGenerationProgress
				? simulateAnimationProgress(
						commentaryGenerationProgress.date,
						commentaryGenerationProgress.progress,
						commentaryGenerationProgress.step,
						commentaryGenerationProgress.step !== "preparing" ? 0.175 : 0.02,
					)
				: undefined,
		[commentaryGenerationProgress],
	);

	const { state: fakeLoaderState, ...handles } = useFakeAiLoader({
		autoStart: initialFakeLoaderState && initialFakeLoaderState.progress < 1,
		initialState: initialFakeLoaderState,
		onStateChange({ stepName, progress }) {
			if (portfolioUid) {
				setAnimationProgress((prevAnimationProgress) => {
					const indexOfAnimation = prevAnimationProgress.findIndex((animation) => animation.id === portfolioUid);

					if (indexOfAnimation > -1) {
						prevAnimationProgress[indexOfAnimation].progress = progress;
						prevAnimationProgress[indexOfAnimation].step = stepName;
						prevAnimationProgress[indexOfAnimation].date = new Date();
						return prevAnimationProgress;
					}

					return [...prevAnimationProgress, { id: portfolioUid, progress, step: stepName, date: new Date() }];
				});
			}
		},
	});

	const handlesRef = useUnsafeUpdatedRef(handles);
	const stopFakeLoader = useCallback(async () => {
		await handlesRef.current.stop();
		setAnimationProgress((animations) => animations.filter((animation) => animation.id !== portfolioUid));
	}, [handlesRef, portfolioUid, setAnimationProgress]);
	const restartFakeLoader = useCallback(async () => {
		await handlesRef.current.stop();
		handlesRef.current.start();
	}, [handlesRef]);

	return {
		fakeLoaderState,
		stopFakeLoader,
		restartFakeLoader,
	};
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function usePortfolioCommentary({
	portfolio,
	enhanced,
	asOf,
	asOfSnapshot,
}: {
	portfolio: InvestmentSummary;
	enhanced: boolean;
	/** As-of date the page is pinned to, if any — the commentary stored for that date is read instead. */
	asOf?: string;
	asOfSnapshot?: AsOfSnapshotState;
}) {
	const { fakeLoaderState, stopFakeLoader, restartFakeLoader } = usePersistentFakeAiLoaderAnimation(
		portfolio.uuid ?? "",
	);

	const previousCommentaryGenerationStatusRef = useRef<InvestmentCommentaryResponseStatusEnum | null>(null);
	const [cachedCommentaryStatus, _setCommentaryGenerationStatus] =
		useState<InvestmentCommentaryResponseStatusEnum | null>(null);
	const setCachedCommentaryStatus = useCallback((v: typeof cachedCommentaryStatus) => {
		_setCommentaryGenerationStatus((cur) => {
			previousCommentaryGenerationStatusRef.current = cur;
			return v;
		});
	}, []);

	const aiHandlerLoaderRef = useUnsafeUpdatedRef({
		stopFakeLoader,
		restartFakeLoader,
	});

	useEffect(() => {
		if (!previousCommentaryGenerationStatusRef.current) {
			return;
		}
		if (cachedCommentaryStatus !== "CALCULATING") {
			aiHandlerLoaderRef.current.stopFakeLoader().catch(noop);
		} else if (
			previousCommentaryGenerationStatusRef.current !== "CALCULATING" &&
			cachedCommentaryStatus === "CALCULATING"
		) {
			aiHandlerLoaderRef.current.restartFakeLoader().catch(noop);
		}
	}, [cachedCommentaryStatus, aiHandlerLoaderRef]);

	const commentaryTemplatesQuery = storyfolioRepository.templates.useQuery();
	const commentaryQuery = useQuery({
		...portfolioRepository.commentary.getQueryConfig(portfolio.uuid ?? "", {
			enhanced,
			status: portfolio.status ?? "READY",
			asOf,
		}),
		// A snapshot's commentary is whatever was stored for that date; it is never being written now, so
		// the live view's poll for a CALCULATING commentary has nothing to wait for here.
		refetchInterval:
			asOf == null && (cachedCommentaryStatus === null || cachedCommentaryStatus === "CALCULATING")
				? seconds(10)
				: false,
		enabled: Boolean(portfolio.uuid) && isAsOfReadable(asOf, asOfSnapshot),
	});

	useQueryReactor(commentaryQuery, {
		onSuccess(data) {
			if (data.status) {
				setCachedCommentaryStatus(data.status);
				if (!previousCommentaryGenerationStatusRef.current) {
					previousCommentaryGenerationStatusRef.current = data.status ?? null;
				}
			}
		},
	});

	const generateCommentaryNonBlocking = useCallback(
		(templateUuid: string) => {
			let sanitizedTemplateUuid = templateUuid;
			const templates = commentaryTemplatesQuery.data;
			if (templates && !templates.find((x) => x.uuid === templateUuid)) {
				sanitizedTemplateUuid = templates.find((x) => x.name === "Standard Template")?.uuid ?? templateUuid;
			}

			setCachedCommentaryStatus("CALCULATING");
			if (enhanced) {
				portfolioRepository
					.createCommentaryFromTemplate(portfolio, {
						templateUuid: sanitizedTemplateUuid,
						enhanced: true,
					})
					.catch((err) => {
						reportPlatformError(err, "WARN", "portfolio", {
							message: "createCommentaryForEnhancementWithTemplate failed",
							payload: JSON.stringify({
								uuid: portfolio.uuid,
								sanitizedTemplateUuid,
							}),
						});
					});
			} else {
				portfolioRepository
					.createCommentaryFromTemplate(portfolio, { templateUuid: sanitizedTemplateUuid, enhanced: false })
					.catch(noop);
			}
		},
		[commentaryTemplatesQuery.data, enhanced, portfolio, setCachedCommentaryStatus],
	);

	const templateOptions = useMemo(
		(): Array<Option<string>> =>
			commentaryTemplatesQuery.data?.flatMap((x) =>
				x.name && x.uuid && x.visible ? [{ label: x.name, value: x.uuid }] : [],
			) ?? [],
		[commentaryTemplatesQuery.data],
	);

	return useMemo(
		() => ({
			fakeLoaderState,
			commentaryQuery,
			templateOptions,
			generateCommentaryNonBlocking,
			cachedCommentaryStatus,
			commentaryTemplatesQuery,
		}),
		[
			fakeLoaderState,
			commentaryQuery,
			templateOptions,
			generateCommentaryNonBlocking,
			cachedCommentaryStatus,
			commentaryTemplatesQuery,
		],
	);
}

const PortfolioCommentaryMDBlock = (ctx: ContextContent<typeof PortfolioContext>) => {
	const { portfolio, enhanced, workspaceUid, asOf } = ctx;
	const user = useUserValue();

	const { t } = useTranslation();

	// POLLING STATUS ogni 60sec solo se commetaryStatus CALCULATING
	// se arriva commentary-update triggerare subito refetch

	const { push } = useTypedNavigation();

	const {
		commentaryQuery,
		commentaryTemplatesQuery,
		fakeLoaderState,
		generateCommentaryNonBlocking,
		cachedCommentaryStatus,
		templateOptions,
	} = usePortfolioCommentary({
		portfolio: portfolio ?? {},
		enhanced,
		asOf,
		asOfSnapshot: ctx.asOfSnapshot,
	});

	// Commentary can only be exported once it actually exists and is no longer being (re)generated.
	const canDownloadCommentary = Boolean(
		portfolio?.uuid && commentaryQuery.data?.commentary && cachedCommentaryStatus !== "CALCULATING",
	);

	const exportCommentary = useCallback(
		() =>
			runWithErrorReporting(
				() =>
					portfolioRepository.exportCommentary({ uuid: portfolio?.uuid, name: portfolio?.name }, { enhanced, asOf }),
				{
					area: "portfolio",
					attemptedOperation: {
						message: "export portfolio commentary",
						payload: JSON.stringify({ uuid: portfolio?.uuid, enhanced }),
					},
				},
			),
		[portfolio?.uuid, portfolio?.name, enhanced, asOf],
	);

	useWidgetOptions(
		() => ({
			actionHeader: () => (
				<div className="flex items-center gap-2">
					{canDownloadCommentary && (
						<DropdownMenu
							Trigger={WidgetDownloadTrigger}
							extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioCommentary.download }}
							actions={[
								{
									icon: "commentary-text",
									label: enhanced ? t("PROPOSAL_COMMENTARY.TITLE") : t("CURRENT_COMMENTARY.TITLE"),
									onClickAsync: exportCommentary,
									"data-qualifier": qualifier.widgets.portfolioCommentary.downloadItem,
								},
							]}
						/>
					)}
					{hasAccess(user, { requiredServices: ["PORTFOLIO_STUDIO_COMMENTARY_TAB"] }) && (
						<Text
							color={themeCSSVars.palette_N500}
							type="Body/M/Bold"
							as="button"
							onClick={() => {
								if (workspaceUid) {
									push("PortfolioDetailsWithWorkspace", {
										workspaceUid,
										portfolioUid: portfolio?.uuid ?? "",
										proposal: String(enhanced),
										tab: PortfolioDetailsTabs.COMMENTARY,
										asOf,
									});
								} else {
									push("PortfolioDetails", {
										portfolioUid: portfolio?.uuid ?? "",
										proposal: String(enhanced),
										tab: PortfolioDetailsTabs.COMMENTARY,
										asOf,
									});
								}
							}}
						>
							Open in tab
						</Text>
					)}
				</div>
			),
			title: enhanced ? t("PROPOSAL_COMMENTARY.TITLE") : t("CURRENT_COMMENTARY.TITLE"),
		}),
		[t, enhanced, asOf, user, push, portfolio?.uuid, workspaceUid, canDownloadCommentary, exportCommentary],
	);
	// This widget wraps `ReactQueryWrapperBase` directly rather than going through
	// `PortfolioQueryWidgetBase`, so it repeats that base's gate: the snapshot's wall is decided above the
	// query, a generating snapshot is the only one of its states that counts as a read, and behind the
	// wall the card withholds the header — the query the wall disables keeps the data it already held,
	// so the download would otherwise stay, and "Open in tab" with it.
	const asOfWall = useAsOfSnapshotWall();
	useWidgetLoading(asOfWall ? ctx.asOfSnapshot?.kind === "generating" : commentaryQuery.isLoading);
	useWidgetWalled(asOfWall != null);

	const widgetDataQuery = useMappedQuery(
		(commentaryData) => {
			if (
				(commentaryData.status === "CALCULATING" || portfolio?.status === "CALCULATING") &&
				!commentaryData.commentary
			) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.CALCULATING as const,
				};
			}
			if (!commentaryData.commentary) {
				return {
					data: { ...commentaryData, templates: commentaryTemplatesQuery.data ?? [] },
					widgetStatus: WidgetStatus.EMPTY as const,
				};
			}
			return {
				data: { ...commentaryData, templates: commentaryTemplatesQuery.data ?? [] },
				widgetStatus: WidgetStatus.READY as const,
			};
		},
		[commentaryTemplatesQuery.data, portfolio?.status],
		commentaryQuery,
	);

	// Above the query wrapper, because a snapshot that is generating or failed leaves the query below
	// disabled, and a disabled query sits at `isLoading` — the loading fallback would hold the card for
	// as long as the date is pinned, saying "calculating" about a date that has already given up.
	if (asOfWall) {
		return <div className="flex flex-col min-h-0 grow">{asOfWall}</div>;
	}

	return (
		<div className="flex flex-col min-h-0 grow">
			<ReactQueryWrapperBase
				query={widgetDataQuery}
				loadingFallback={<IconWalls.CalculatingApi />}
				errorFallback="Generative AI commentary is not available at the moment. It will be generated soon!"
			>
				{(widgetData) => {
					return (
						<PortfolioWidgetBase
							{...widgetData}
							iconWalls={{
								empty: IconWalls.PortfolioCommentaryEmpty({
									onGenerate: (templateId) => {
										if (templateId) {
											generateCommentaryNonBlocking(templateId);
											return;
										}

										platformToast({
											children: "Unable to generate portfolio commentary",
											severity: "error",
											icon: "Ask-ai",
										});
									},
									generateOption: templateOptions,
									defaultValue: commentaryQuery.data?.template?.uuid,
								}),
							}}
						>
							{(response) => {
								const { templates, template } = response;

								return (
									<PortfolioCommentaryBodyMDBlockV2
										onGenerate={generateCommentaryNonBlocking}
										ctx={ctx}
										data={response}
										templates={templates}
										selected={template!.uuid}
										cachedCommentaryStatus={cachedCommentaryStatus}
										loaderProps={{
											state: fakeLoaderState,
										}}
									/>
								);
							}}
						</PortfolioWidgetBase>
					);
				}}
			</ReactQueryWrapperBase>
		</div>
	);
};

const markdownOverrides: MarkdownRendererProps["componentOverrides"] = {
	table: ({ node: _node, ...props }) => <table className="w-full border-collapse" {...props} />,
	thead: ({ node: _node, ...props }) => <thead {...props} />,
	tr: ({ node: _node, ...props }) => (
		<tr className={`even:bg-[#F7F8F9] border-b border-b-[color:var(--palette-N100)]`} {...props} />
	),
	td: ({ node: _node, ...props }) => <td className="text-left p-2 text-[10px]!" {...props} />,
	th: ({ node: _node, ...props }) => (
		<th className="text-left px-2 py-1 font-bold! text-[10px]! uppercase! text-[#667085]" {...props} />
	),
	tbody: ({ node: _node, ...props }) => <tbody {...props} />,
};

export default withContext(PortfolioContext)(PortfolioCommentaryMDBlock);

export const reapplySphereMetricsBannerExpandDefault = true;
export const reapplySphereMetricsBannerExpand$ = atomWithStorage<boolean>(
	"reapplySphereMetricsBannerExpandAtom",
	reapplySphereMetricsBannerExpandDefault,
);

export function useReapplySphereMetricsBannerExpandState(): {
	expand: boolean;
	setExpand: SetterOrUpdater<boolean>;
	reset(): void;
} {
	const [expand, setExpand] = useAtom(reapplySphereMetricsBannerExpand$);
	return {
		expand,
		setExpand,
		reset: () => setExpand(reapplySphereMetricsBannerExpandDefault),
	};
}

export function ReapplySphereMetricsBanner(
	props: { portfolio: InvestmentSummary; metadataLastUpdate?: string } & StylableProps,
): React.JSX.Element {
	const [expand, setExpand] = useAtom(reapplySphereMetricsBannerExpand$);
	const { formatDateTime } = useLocaleFormatters();

	return (
		<Banner
			severity="info"
			title={
				<ClickableDiv className="-mb-1" onClick={() => setExpand(!expand)}>
					<Row alignItems="center" gap={8} justifyContent="space-between">
						<Row alignItems="center" gap={8}>
							<Text type="Body/M/Bold">Imported metrics in use</Text>
							<Text type="Body/M/Book">
								{props.metadataLastUpdate ? ` (last import: ${formatDateTime(props.metadataLastUpdate)})` : ""}
							</Text>
						</Row>
						<div className="transition-transform aria-expanded:transform-[rotateX(180deg)] flex" aria-expanded={expand}>
							<Icon icon="Down" size={20} color={themeCSSVars.palette_N500} />
						</div>
					</Row>
				</ClickableDiv>
			}
			{...props}
		>
			<CollapsibleBase expand={expand}>
				<Column gap={4} className="pt-1">
					<Text type="Body/M/Book">The commentary reflects only the data provided through your imported metrics</Text>
					<Row justifyContent="end">
						{props.portfolio.uuid && props.portfolio.name && (
							<ActionText
								disabled={!expand}
								type="Body/S/Bold"
								onClick={unpromisify(() =>
									reapplySphereMetricsWithConfirmationDialog({
										uuid: props.portfolio.uuid,
										name: props.portfolio.name,
									}),
								)}
							>
								Reapply Sphere metrics
							</ActionText>
						)}
					</Row>
				</Column>
			</CollapsibleBase>
		</Banner>
	);
}
